import { WebStorageStateStore } from 'oidc-client-ts'
import { createOidcAuth, LogLevel, SignInType } from './vue-oidc-client-patch'
import { useUserStore } from '@/stores/useUserStore'
import { useActivitiesStore } from '@/stores/useActivityStore'
import { Configuration, DefaultConfig } from '@/api-swagger'
import * as Sentry from '@sentry/vue'
import { useSelectionsStore } from '@/stores/useSelectionsStore'
import { useCampaignStore } from '@/stores/useCampaignStore'
import { useTitleStore } from '@/stores/useTitleStore'
import { createBoostCoreFetch } from '@/utils/dPopFetch'
import { useAuthStore } from '@/stores/useAuthStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useOrderStore } from '@/stores/useOrderStore'
import { initCoreSessionHandler } from '@/auth/coreSessionHandler'

const secureLogger: Console = {
  ...console,
  error: (message: string, ...args: any[]) => {
    const safeArgs = args.map((arg) => {
      if (typeof arg === 'string' && (arg.includes('token') || arg.includes('password'))) {
        return '[REDACTED]'
      }
      return arg
    })
    console.error(message, ...safeArgs)
  },
  warn: console.warn,
  info: console.info,
  debug: (message: string, ...args: any[]) => {
    if (import.meta.env.DEV) {
      console.debug(message, ...args)
    }
  },
}

const tenantId = import.meta.env.VITE_AZURE_AD_TENANT_ID || '18fa1534-e40a-495f-952e-52aad3dbbf05'

// Live origin so redirect_uri matches whichever allowed domain the user
// is currently on. Multi-domain gating is enforced at boot in main.ts via
// isOriginAllowed(); Azure AD rejects unregistered redirect URIs as second
// line of defense.
const appOrigin = window.location.origin

const oidcAuth = createOidcAuth(
  'main',
  SignInType.Window,
  appOrigin,
  {
    authority: `https://login.microsoftonline.com/${tenantId}/v2.0`,
    redirect_uri: `${appOrigin}/oidc-callback`,
    client_id: import.meta.env.VITE_CLIENT_ID_AZURE,
    scope: 'openid profile offline_access User.ReadBasic.All',
    response_type: 'code',
    automaticSilentRenew: true,
    // Renew 5 min before expiry instead of the default 60 s — leaves enough
    // margin for slow networks or busy machines (B2B multi-tab usage).
    accessTokenExpiringNotificationTimeInSeconds: 300,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
    monitorSession: false,
    silentRequestTimeoutInSeconds: 15,
  },
  secureLogger,
  import.meta.env.DEV ? LogLevel.Debug : LogLevel.Error,
)

let isInitialUserLoad = true
let recoverInProgress = false

/**
 * Bare /me probe with no retry, no recovery loop. Safe to call from inside
 * a Web Lock — won't try to re-acquire it via tryRefreshSession.
 */
async function rawPingMe(): Promise<boolean> {
  try {
    const url = import.meta.env.VITE_SUITE_URL + '/auth/user/me'
    const res = await fetch(url, { method: 'GET', credentials: 'include' })
    return res.ok
  } catch {
    return false
  }
}

export async function syncTokensWithCore(user: { access_token: string; profile: any }) {
  // Atomic session rotation, single-flight across tabs (Web Lock). The lock
  // body stays minimal: only the rotation primitives. API calls that route
  // through suiteFetch's own retry path (fetchMe, ensureOrderModels…) MUST
  // happen *outside* the lock — otherwise a 401 from one of them would try
  // to re-acquire this same lock via tryRefreshSession, causing a re-entrant
  // deadlock.
  await navigator.locks.request('boost-core-refresh', async () => {
    // Smart early-out: if another tab rotated very recently, verify the cookie
    // still works via a raw ping (no retry path, can't deadlock). When it
    // works we skip rotation entirely — that's how concurrent tabs land on
    // the same single rotation: first tab does the work, others verify+skip.
    try {
      const raw = localStorage.getItem('coreRefreshState')
      if (raw) {
        const state = JSON.parse(raw) as { expiresAt?: number }
        if (state.expiresAt && state.expiresAt > Date.now() + 60_000) {
          if (await rawPingMe()) return
          // Ping failed despite recent rotation (admin revoked, etc.) → fall
          // through to full rotate.
        }
      }
    } catch { /* ignore */ }

    const authStore = useAuthStore()
    // Try refresh first to rotate the existing AccessToken. Fall back to
    // coreLogin only if no valid boostcore session exists (first login, cookie
    // cleared, absoluteExpiresAt passed, or session revoked).
    const refreshed = await authStore.coreRefresh()
    if (!refreshed) {
      await authStore.coreLogin(user.access_token, user.profile.oid)
    }
  })

  const boostCoreStore = useBoostCorePermissionStore()
  await boostCoreStore.fetchMe()

  if (boostCoreStore.canAccessOrders) {
    const orderStore = useOrderStore()
    void Promise.all([orderStore.ensureOrderModels(), orderStore.ensureVentilationFamilies()]).catch(() => {})
  }
}

/**
 * Attempt silent recovery with retries before falling back to interactive login.
 * Prevents multiple concurrent recovery attempts via the recoverInProgress guard.
 */
async function tryRecoverSession(maxRetries = 2): Promise<boolean> {
  if (recoverInProgress) return false
  recoverInProgress = true
  try {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const user = await oidcAuth.mgr.signinSilent()
        if (user) {
          await syncTokensWithCore(user)
          return true
        }
      } catch {
        if (attempt < maxRetries - 1) {
          await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)))
        }
      }
    }
    return false
  } finally {
    recoverInProgress = false
  }
}

let refreshPromise: Promise<boolean> | null = null

export async function refreshBoostCoreSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      let user = await oidcAuth.mgr.getUser()
      if (!user) return false
      if (user.expired) {
        user = await oidcAuth.mgr.signinSilent()
        if (!user) return false
      }
      // syncTokensWithCore acquires the 'boost-core-refresh' lock internally.
      // Don't wrap it here — that would deadlock (re-acquiring an exclusive lock
      // already held by the outer scope). The early-out + fetchMe-on-success path
      // inside syncTokensWithCore handles the recently-rotated and revoked-session
      // cases.
      await syncTokensWithCore(user)
      return true
    } catch {
      return false
    } finally {
      refreshPromise = null
    }
  })()
  return refreshPromise
}

async function clearCoreSession() {
  Sentry.setUser(null)
  const authStore = useAuthStore()
  // Passive cleanup: this runs in response to an Azure event (addUserUnloaded
  // / addUserSignedOut), which fires in every tab when one tab clears its
  // localStorage. We must NOT re-broadcast — the originating tab already did.
  await authStore.coreLogout({ silent: true })
  const boostCoreStore = useBoostCorePermissionStore()
  boostCoreStore.clear()
  useOrderStore().clearOrderCatalog()
}

// ── Events ──────────────────────────────────────────────────────────────

oidcAuth.events.addAccessTokenExpiring(function () {
  secureLogger.info('Access token expiring, will attempt to renew')
})

oidcAuth.events.addAccessTokenExpired(async function () {
  // If the user has signed out (no OIDC user in storage) the silent-renew
  // timer should already be stopped — but be defensive: skip recovery, don't
  // open an Azure iframe / popup behind the scenes.
  const stillSignedIn = await oidcAuth.mgr.getUser().catch(() => null)
  if (!stillSignedIn) return
  secureLogger.warn('Access token expired, attempting silent recovery')
  const recovered = await tryRecoverSession()
  if (!recovered) {
    secureLogger.warn('Recovery failed, redirecting to login')
    oidcAuth.signIn()
  }
})

oidcAuth.events.addSilentRenewError(async function (err: Error) {
  const stillSignedIn = await oidcAuth.mgr.getUser().catch(() => null)
  if (!stillSignedIn) return
  secureLogger.error('Silent renew error, attempting recovery', err.message)
  const recovered = await tryRecoverSession()
  if (!recovered) {
    secureLogger.error('Recovery failed after silent renew error, redirecting to login')
    oidcAuth.signIn()
  }
})

oidcAuth.events.addUserLoaded(async function (user) {
  if (!isInitialUserLoad) {
    secureLogger.info('Token silently renewed, syncing with BoostCore')
    try {
      // syncTokensWithCore refreshes the core_session cookie + CSRF via
      // authStore.coreLogin(); createBoostCoreFetch reads CSRF dynamically
      // from authStore on every request, so no fetch reconstruction needed.
      await syncTokensWithCore(user)
    } catch (err) {
      secureLogger.error(
        'Error syncing renewed token with BoostCore',
        err instanceof Error ? err.message : 'Unknown error',
      )
    }
    return
  }

  isInitialUserLoad = false

  try {
    await syncTokensWithCore(user)

    DefaultConfig.config = new Configuration({
      basePath: import.meta.env.VITE_SUITE_URL,
      fetchApi: createBoostCoreFetch(),
      credentials: 'include',
    })

    Sentry.setUser({
      id: user.profile.sub,
      email: user.profile.email,
      username: user.profile.name,
    })

    const userStore = useUserStore()
    try {
      const res = await userStore.getMe()
      if (res) {
        initCoreSessionHandler(refreshBoostCoreSession)
        initAuthListeners()

        userStore.userConnected = res

        const activityStore = useActivitiesStore()
        const selectionsStore = useSelectionsStore()
        const campaignStore = useCampaignStore()
        const titleStore = useTitleStore()

        await Promise.all([
          userStore.getAllUsers(),
          activityStore.getActivitiesByUserId(userStore.userConnected?.id),
          selectionsStore.getAll(),
          campaignStore.getMyPrivateCampaign(),
          campaignStore.getManagerCampaigns(),
          titleStore.getTitles(),
        ]).catch((err) => {
          secureLogger.error('Error initializing stores', err.message)
        })
      }
    } catch (err) {
      secureLogger.error('Error loading user data', err instanceof Error ? err.message : 'Unknown error')
    }
  } catch (err) {
    secureLogger.error(
      'Error during user authentication setup',
      err instanceof Error ? err.message : 'Unknown error',
    )
  }
})

oidcAuth.events.addUserUnloaded(async function () {
  secureLogger.debug('User unloaded, clearing BoostCore session')
  isInitialUserLoad = true
  await clearCoreSession()
})

oidcAuth.events.addUserSignedOut(async function () {
  secureLogger.info('User signed out')
  await clearCoreSession()
})

oidcAuth.events.addUserSessionChanged(function () {
  secureLogger.debug('User session changed')
})

// ── Tab visibility & network recovery ───────────────────────────────────

export function initAuthListeners() {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return
    try {
      const user = await oidcAuth.mgr.getUser()
      if (!user) return
      if (user.expired) {
        secureLogger.info('Tab resumed with expired token, refreshing')
        await refreshBoostCoreSession()
      }
    } catch {
      // Next API call will trigger refresh via 401 handler if needed
    }
  })

  window.addEventListener('online', async () => {
    secureLogger.info('Network restored, checking session')
    try {
      const user = await oidcAuth.mgr.getUser()
      if (!user || user.expired) {
        await refreshBoostCoreSession()
      }
    } catch {
      // Silently ignore
    }
  })
}

export default oidcAuth
